import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Chip,
  FormControlLabel,
  Grid,
  Link,
  Skeleton,
  Typography,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useEffect, useState } from "react";

import StyledSwitch from "components/Styled/Switch";
import QuestionBox from "components/Styled/VAPage/QuestionBox";
import StyledProPage from "features/Auth/Onboarding/ProOnboarding/StyledPage";
import { fetcherAuth } from "utils/api";

interface Props {
  handleNext: () => void;
  handleBack: () => void;
  onFinishSetUpLater?: () => void;
}

const AutopilotOnboardingImportContacts = ({
  handleNext,
  handleBack,
  onFinishSetUpLater,
}: Props) => {
  const { getToken } = useAuth();
  const { setErrorAlert } = useContext(AlertContext);
  const { currentOrg, setCurrentOrg } = useContext(OrganizationUserContext);

  const [loading, setLoading] = useState(false);
  const [importBrandCount, setImportBrandCount] = useState<string | null>(null);
  const [allowImportEmail, setAllowImportEmail] = useState(true);

  const getNumberOfContacts = async () => {
    if (process.env.NODE_ENV === "development") {
      setImportBrandCount("6,500");
      return;
    }
    try {
      const { count } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/bento-brands/import-contacts/total`,
        "GET",
      );
      setImportBrandCount((Math.round(count / 100) * 100).toLocaleString());
    } catch {
      setImportBrandCount("6,500");
    }
  };

  const saveAndContinue = async () => {
    setLoading(true);
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/outreach-automation-settings/import-contacts`,
        "PUT",
        {},
        {
          allowImportEmail,
          allowShareContacts: currentOrg?.allowShareContacts,
        },
      );
      handleNext();
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentOrg?.id) {
      getNumberOfContacts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  const body = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexDirection: "column",
        width: "100%",
      }}
    >
      <QuestionBox styles={{ maxWidth: 600, width: "100%" }}>
        <FormControlLabel
          sx={{ mb: 2 }}
          control={
            <StyledSwitch
              checked={allowImportEmail}
              onChange={(e) => {
                setAllowImportEmail(e.target.checked);
                if (!e.target.checked) {
                  setCurrentOrg((prev) =>
                    prev ? { ...prev, allowShareContacts: false } : prev,
                  );
                }
              }}
            />
          }
          label={
            <Typography sx={{ fontSize: { xs: 14, md: 18 }, ml: 1 }}>
              Import your past partnerships from email
            </Typography>
          }
        />

        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          We'll scan your contacts to find brands you've already worked with, so
          we never pitch them again. This keeps your relationships positive and
          professional.
        </Typography>
      </QuestionBox>

      <QuestionBox
        styles={{
          maxWidth: 600,
          width: "100%",
          mt: 2,
          opacity: allowImportEmail ? 1 : 0.5,
          pointerEvents: allowImportEmail ? "auto" : "none",
        }}
      >
        <FormControlLabel
          sx={{ mb: 2 }}
          control={
            <StyledSwitch
              checked={allowImportEmail && currentOrg?.allowShareContacts}
              disabled={!allowImportEmail}
              onChange={(e) => {
                setCurrentOrg((prev) =>
                  prev
                    ? { ...prev, allowShareContacts: e.target.checked }
                    : prev,
                );
              }}
            />
          }
          label={
            <Typography sx={{ fontSize: { xs: 14, md: 18 }, ml: 1 }}>
              Join the creator network for more brand opportunities
            </Typography>
          }
        />

        <Typography>
          Share your brand contacts anonymously to unlock{" "}
          <strong>
            {<>{importBrandCount}+</> || <Skeleton sx={{ width: 100 }} />}{" "}
          </strong>
          from other creators in Bento's network. Your contacts won't be linked
          to you or reveal any partnership details.
        </Typography>

        <Grid
          container
          justifyContent="flex-start"
          alignItems="center"
          sx={{ mt: 2 }}
          gap={1}
        >
          <Chip
            size="small"
            label={
              <>
                <Box
                  component="i"
                  className="fa-solid fa-key"
                  sx={{ mr: 0.5 }}
                />
                Fully anonymous
              </>
            }
            sx={{
              fontSize: 12,
              backgroundColor: "white",
              color: "purple.dark",
              border: `1px solid`,
              borderColor: "purple.dark",
              width: "fit-content",
            }}
          />
          {importBrandCount ? (
            <Chip
              size="small"
              label={
                <>
                  <Box
                    component="i"
                    className="fa-solid fa-database"
                    sx={{ mr: 0.5 }}
                  />
                  Access to {importBrandCount}+ brands
                </>
              }
              sx={{
                fontSize: 12,
                backgroundColor: "white",
                color: "purple.dark",
                border: `1px solid`,
                borderColor: "purple.dark",
                width: "fit-content",
              }}
            />
          ) : (
            <></>
          )}
        </Grid>
      </QuestionBox>

      <Box sx={{ mt: 2 }}>
        All data is encrypted and secure. We never share your personal
        information. Read our{" "}
        <Link
          sx={{ textDecoration: "none", color: "purple.dark" }}
          href="https://storage.googleapis.com/hatchways-crm.appspot.com/app-assets/Bento's%20Import%20Contacts%20Agreement-%20June%202025.pdf"
          target="_blank"
          rel="noreferrer"
        >
          Terms of Service
        </Link>
      </Box>
    </Box>
  );

  return (
    <StyledProPage
      title="Import Contacts"
      description="Import your past brand partnerships and join the creator network"
      onBack={handleBack}
      onNext={saveAndContinue}
      buttonNext="Continue"
      buttonBack="Back"
      body={body}
      stepNumber={3}
      loading={loading}
      onFinishSetUpLater={onFinishSetUpLater}
      splitScreen={false}
    />
  );
};

export default AutopilotOnboardingImportContacts;
